import { commandRefusal, spendWill } from "../court/king";
import { type AxialCoord, hexEquals } from "../hex";
import { findHex, isPlainOwnHex, patchHexAt } from "../run/board";
import { kingOf, spend, supplyOf } from "../run/pools";
import { sameOwner } from "../run/sides";
import { settle } from "../run/status";
import type { RunRules } from "../types/rules";
import type { BuildKind, FactionId, HexState, PaidCommand, Role, RunState } from "../types/state";
import { disbandOrphans } from "../units/movement";
import { cityTooClose, inCityReach, wallMax } from "./cities";

/**
 * Building on the land: the build verb (tower, city, farm, hamlet), its price and time, the finished
 * builds (tick step 1b), the improvement a builder picks, and razing.
 */

/** A farm or a hamlet: bought on land in reach, paid for with `costs.improve` Will by a King. */
export function isImprovement(kind: BuildKind): kind is "farm" | "hamlet" {
  return kind === "farm" || kind === "hamlet";
}

/** The court command a build of this kind is. */
export function buildCommand(kind: BuildKind): PaidCommand {
  return isImprovement(kind) ? "improve" : kind;
}

/** Supply a faction spends on a build of this kind; an improvement's is 0 without the block. */
export function buildPrice(rules: RunRules, kind: BuildKind): number {
  if (isImprovement(kind)) {
    return rules.improvements?.cost ?? 0;
  }
  return kind === "city" ? rules.cityCost : rules.buildCost;
}

/** Ticks a build of this kind takes from its order to its ready tick. */
export function buildDuration(rules: RunRules, kind: BuildKind): number {
  if (isImprovement(kind)) {
    return rules.improvements?.buildTicks ?? 0;
  }
  return kind === "city" ? rules.cityBuildTicks : rules.buildTicks;
}

/**
 * The build verb's precondition: own, plain, unrazed, the faction's pool holds the kind's
 * price, for a city no other city inside `cityMinSpacing`, and for an improvement the block
 * and a working own city that reaches the hex (A54: it pays only there, like the land).
 */
export function isBuildable(
  state: RunState,
  faction: FactionId,
  coord: AxialCoord,
  kind: BuildKind,
  issuer: Role = "king",
): boolean {
  const hex = findHex(state, coord);
  return (
    state.status === "running" &&
    isPlainOwnHex(hex, faction) &&
    supplyOf(state, faction) >= buildPrice(state.rules, kind) &&
    (kind !== "city" || !cityTooClose(state, coord)) &&
    (!isImprovement(kind) || (state.rules.improvements !== null && inCityReach(state, coord))) &&
    commandRefusal(state, faction, coord, buildCommand(kind), issuer) === undefined
  );
}

/**
 * Build on an own hex. The price leaves the pool at once; the structure works after the
 * kind's build time. Returns the same object when rejected.
 */
export function buildStructure(
  state: RunState,
  faction: FactionId,
  coord: AxialCoord,
  kind: BuildKind,
  issuer: Role = "king",
): RunState {
  if (!isBuildable(state, faction, coord, kind, issuer)) {
    return state;
  }
  const paid = spendWill(
    spend(state, faction, buildPrice(state.rules, kind)),
    faction,
    buildCommand(kind),
    issuer,
  );
  return patchHexAt(paid, coord, {
    structure: kind,
    structureReadyTick: state.tick + buildDuration(state.rules, kind),
  });
}

/** Step 1b: a build that reaches its ready tick starts working; a tower raises its walls at once. */
export function completeBuilds(state: RunState): RunState {
  let changed = false;
  const hexes = state.hexes.map((hex) => {
    if (hex.structureReadyTick === 0 || hex.structureReadyTick > state.tick) {
      return hex;
    }
    changed = true;
    const done = { ...hex, structureReadyTick: 0 };
    return { ...done, walls: Math.max(done.walls, wallMax(done, state.rules, state.tick)) };
  });
  return changed ? { ...state, hexes } : state;
}

/**
 * The improvement a faction builds next (A54): a farm and a hamlet in turn over the land
 * given, a hamlet once the farms outnumber them; farms only without the population block,
 * where a hamlet would grow nothing.
 */
export function nextImprovement(state: RunState, land: readonly HexState[]): BuildKind {
  const farms = land.filter((hex) => hex.structure === "farm").length;
  const hamlets = land.filter((hex) => hex.structure === "hamlet").length;
  return state.rules.population && farms > hamlets ? "hamlet" : "farm";
}

/**
 * A faction burns one of its own hexes into a wall. Any unit standing on it is lost with it,
 * so is a structure or a build in progress. With a court it is a command like any other, and
 * the ground the King stands on is never burned.
 */
export function razeHex(state: RunState, faction: FactionId, coord: AxialCoord): RunState {
  if (state.status !== "running") {
    return state;
  }
  const target = findHex(state, coord);
  if (target === undefined || !sameOwner(target.owner, faction) || target.razed) {
    return state;
  }
  const king = kingOf(state, faction);
  if (
    (king && hexEquals(king.coord, coord)) ||
    commandRefusal(state, faction, coord, "raze") !== undefined
  ) {
    return state;
  }
  const burned = patchHexAt(spendWill(state, faction, "raze"), coord, {
    owner: "none",
    razed: true,
    walls: 0,
    structure: "none",
    structureReadyTick: 0,
    unitBuild: "none",
    unitBuildReadyTick: 0,
    mayor: false,
  });
  const units = disbandOrphans(state.units.filter((unit) => !hexEquals(unit.coord, coord)));
  // A raze is not a capture: it never triggers sudden death, only elimination.
  return settle({ ...burned, units }, false);
}
